/**
 * /assistant/confirm?action=<id>[&token=<single-use token>]
 *
 * The one place every surface can send the owner to for a parked send: the
 * chat's "waiting for your approval" card links here, an MCP host's deep link
 * lands here, and the Chrome side panel's "Approve in BuzzBit X" opens it.
 * It lives inside the signed-in app on purpose — the session is the proof the
 * confirm route accepts, and no token ever has to reach a panel or a host.
 *
 * A host deep link may carry the single-use token; when it does, the card
 * confirms with it. Otherwise the session plus the hash the ledger handed out
 * is enough.
 *
 * Post-ratchet file: every user-facing string renders via t() from src/i18n.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PendingActionCard } from '../../components/assistant/PendingActionCard';
import { t } from '../../i18n';
import { assistantApi, type PendingConfirmation } from '../../services/api/assistantApi';

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'found'; readonly action: PendingConfirmation }
  | { readonly kind: 'missing' };

export default function ConfirmAction(): JSX.Element {
  const [params] = useSearchParams();
  const actionId = params.get('action');
  const token = params.get('token');
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    if (!actionId) {
      setState({ kind: 'missing' });
      return undefined;
    }
    void assistantApi
      .listPendingActions()
      .then((pending) => {
        if (cancelled) return;
        const action = pending.find((candidate) => candidate.id === actionId);
        setState(action ? { kind: 'found', action } : { kind: 'missing' });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'missing' });
      });
    return () => {
      cancelled = true;
    };
  }, [actionId]);

  return (
    <div className="mx-auto max-w-xl p-6">
      <h1 className="text-lg font-semibold">{t('assistant.confirm.title')}</h1>
      <p className="mt-1 text-sm opacity-80">{t('assistant.confirm.lead')}</p>
      <div className="mt-4">
        {state.kind === 'loading' && (
          <p className="text-sm opacity-70">{t('assistant.confirm.loading')}</p>
        )}
        {state.kind === 'missing' && <p className="text-sm">{t('assistant.confirm.notFound')}</p>}
        {state.kind === 'found' && (
          <PendingActionCard
            action={{
              id: state.action.id,
              toolName: state.action.toolName,
              expiresAt: state.action.expiresAt,
              confirmHash: state.action.confirmHash,
              token,
              inputSummary: state.action.inputSummary,
            }}
          />
        )}
      </div>
      <Link to="/studio" className="mt-6 inline-block text-sm underline">
        {t('assistant.confirm.back')}
      </Link>
    </div>
  );
}
